import React, { useMemo } from "react";
import NavbarDoctorOffice from "../../../NavbarDoctorOffice";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Col,
  Input,
  Label,
  Row,
} from "reactstrap";
import ReactTable from "@dhn/components/molecules/data-table";
import StatusBadge from "@dhn/components/molecules/status-badge";
import useActions from "./useActions";
import moment from "moment";
import ReactFormat from "react-number-format";
import DatePicker from "react-datepicker";
import NoticeBanner from "@dhn/components/molecules/notice-banner";
import DoctorsOfficePage from "../../../components/DoctorsOfficePage";
import { getMonthlyClosingPeriod } from "../../reports/reportContext";
import "../../monthly-closing-motion.css";

const RD_UTC_OFFSET_MINUTES = -4 * 60;
const DAILY_CLOSING_CLOSED_STATUS_ID = 3;

const isDailyClosingClosed = (item = {}) =>
  item?.statusId === DAILY_CLOSING_CLOSED_STATUS_ID;

// Misma regla que valida el API: todo cierre diario del mes debe estar cerrado y seleccionado.
const isDailyClosingReady = (item = {}) =>
  isDailyClosingClosed(item) && item?.selected !== false;

const toNumber = (value) => {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
};

const hasDailyClosingValues = (item = {}) =>
  toNumber(item.insuredCoverage) > 0 ||
  toNumber(item.totalSales) > 0 ||
  toNumber(item.consultoryFee) > 0;

export default function MonthlyOpenProcess({
  profile,
  user,
  center,
  dataDoctor,
  history,
}) {
  const [{ state, loadingDailyClosings, monthlyClosing }, actions] = useActions({
    history,
    dataDoctor,
    user,
  });

  const {
    handleShowReportDetail,
    handleShowOpenMonthlyClosingReport,
    loadingUpdateMonthlyClosing,
  } = actions;

  const { data } = state;

  // El periodo puede cruzar meses cuando el consultorio tiene un dia de corte configurado.
  const period = useMemo(() => {
    if (monthlyClosing?.month) {
      return getMonthlyClosingPeriod(monthlyClosing);
    }

    const baseDate = data[0]?.date ? moment(data[0].date) : moment();
    return getMonthlyClosingPeriod({
      month: baseDate.month() + 1,
      year: baseDate.year(),
    });
  }, [data, monthlyClosing]);

  const getDaysOfMonth = useMemo(() => {
    const daysArray = [];

    for (
      let day = moment(period.startDate);
      !day.isAfter(period.endDate, "day");
      day.add(1, "day")
    ) {
      const dateString = day.format("YYYY-MM-DD");
      const existingData = data?.find(
        (item) => moment(item?.date).format("YYYY-MM-DD") === dateString,
      );

      if (existingData) {
        daysArray.push(existingData);
      } else {
        daysArray.push({
          date: dateString,
          insuredCoverage: "00.00",
          totalSales: "00.00",
          consultoryFee: "00.00",
          empty: true,
        });
      }
    }
    return daysArray;
  }, [data, period]);

  const visibleDaysOfMonth = useMemo(
    () => getDaysOfMonth.filter(hasDailyClosingValues),
    [getDaysOfMonth],
  );

  const col = useMemo(
    () => [
      {
        Header: "Dia",
        className: "text-center",
        Cell: ({ original }) => moment(original?.date).format("DD/MM"),
      },
      {
        Header: "Ventas Seguro Médico",
        className: "text-right",
        Cell: ({ original }) => {
          return (
            <ReactFormat
              style={{ textAlign: "right", display: "block" }}
              value={original.insuredCoverage || "00.00"}
              thousandSeparator={true}
              fixedDecimalScale={true}
              decimalScale={2}
              displayType="text"
              prefix={"$"}
            />
          );
        },
      },
      {
        Header: "Co-Pagos",
        className: "text-right",
        Cell: ({ original }) => {
          return (
            <ReactFormat
              style={{ textAlign: "right", display: "block" }}
              value={original.totalSales || "00.00"}
              thousandSeparator={true}
              fixedDecimalScale={true}
              decimalScale={2}
              displayType="text"
              prefix={"$"}
            />
          );
        },
      },
      {
        Header: "Total",
        className: "text-right",
        Cell: ({ original }) => {
          return (
            <ReactFormat
              style={{ textAlign: "right", display: "block" }}
              value={original.consultoryFee || "00.00"}
              thousandSeparator={true}
              fixedDecimalScale={true}
              decimalScale={2}
              displayType="text"
              prefix={"$"}
            />
          );
        },
      },
      {
        Header: "Estado de cierre",
        className: "text-center",
        Cell: ({ original }) => {
          const closed = isDailyClosingClosed(original);
          return (
            <StatusBadge
              color={closed ? "success" : "warning"}
              icon={closed ? "fa-lock" : "fa-unlock"}
            >
              {original?.status?.name || (closed ? "Cerrado" : "Pendiente")}
            </StatusBadge>
          );
        },
      },
      {
        Header: "Opciones",
        Cell: ({ original }) => {
          return (
            <Button
              color="secondary"
              hidden={original?.empty}
              className="btn-color-gray card-header-action"
              onClick={() => {
                handleShowReportDetail(original);
              }}
            >
              <i className="fa fa-eye"></i>
              {" Ver detalle"}
            </Button>
          );
        },
      },
    ],
    [handleShowReportDetail],
  );

  const hasPendingDailyClosing =
    !data.length || data.some((item) => !isDailyClosingReady(item));

  // La fecha de habilitacion la calcula el API; el fin del periodo es solo respaldo.
  const monthlyCloseRule = useMemo(() => {
    const closeEnabledDate = moment(
      monthlyClosing?.closeEnabledDate || period.endDate,
    ).utcOffset(RD_UTC_OFFSET_MINUTES, true);
    const nowRD = moment().utcOffset(RD_UTC_OFFSET_MINUTES);

    return {
      canClose: nowRD.isSameOrAfter(closeEnabledDate),
      closeEnabledDateLabel: closeEnabledDate.format("DD/MM/YYYY [a las] hh:mm A"),
      periodLabel: period.periodLabel,
    };
  }, [monthlyClosing?.closeEnabledDate, period]);

  return (
    <div className="animated fadeIn monthly-closing-view-enter">
      <NavbarDoctorOffice
        profile={profile}
        user={user}
        center={center}
        dataDoctor={dataDoctor}
      />

      <DoctorsOfficePage icon="fa-calculator" title="Cierre Mensual" className="monthly-closing-card-enter">
        <Row form>
          <Col md={3}>
            <Label>Centro:</Label>
            <Input value={center} disabled />
          </Col>
          <Col md={4}>
            <Label>Médico</Label>
            <Input disabled value={`${user.firstName} ${user.lastName}`} />
          </Col>
          <Col md={2} className="d-flex flex-column">
            <Label>Mes</Label>
            <DatePicker
              className="form-control text-center"
              disabled
              selected={moment(period.endDate).toDate()}
              dateFormat="MM-yyyy"
              showMonthYearPicker
            />
          </Col>
          <Col md={3}>
            <Label>Período</Label>
            <Input disabled value={period.rangeLabel} className="text-center" />
          </Col>
        </Row>
        <Row form className="my-2">
          <Col>
            <Label className="fw-bold border-bottom">
              Ventas por Seguro
            </Label>{" "}
          </Col>
        </Row>
        <Card>
          <CardBody>
            <Row form>
              <Col className="monthly-closing-table-enter">
                <ReactTable
                  defaultPageSize={10}
                  data={visibleDaysOfMonth}
                  loading={loadingDailyClosings}
                  loadingText="Cargando datos..."
                  noDataText="No hay datos para mostrar"
                  columns={col}
                />
              </Col>
            </Row>
          </CardBody>
          <CardFooter className="d-inline">
            <Row form className="d-flex justify-content-between">
              <Col>
                <Button
                  color="success"
                  className="btn-color-success mr-1"
                  disabled={
                    loadingUpdateMonthlyClosing ||
                    hasPendingDailyClosing ||
                    !monthlyCloseRule.canClose
                  }
                  onClick={() =>
                    handleShowOpenMonthlyClosingReport({
                      getDaysOfMonth: visibleDaysOfMonth,
                    })
                  }
                >
                  <i className="fa fa-check-circle"></i>
                  {" Procesar Cierre"}
                </Button>
                <Button
                  color="danger"
                  className="btn-color-danger"
                  onClick={() => history.goBack()}
                >
                  <i className="fa fa-times"></i>
                  {" Cerrar"}
                </Button>
              </Col>
              {!monthlyCloseRule.canClose && (
                <Col className="d-flex justify-content-end">
                  <NoticeBanner tone="danger" icon="fa-calendar-times-o">
                    Este mes ({monthlyCloseRule.periodLabel}) solo puede cerrarse a
                    partir del {monthlyCloseRule.closeEnabledDateLabel} (hora RD).
                  </NoticeBanner>
                </Col>
              )}
            </Row>
          </CardFooter>
        </Card>
      </DoctorsOfficePage>
    </div>
  );
}
